import { AssessmentQuestionType } from './types';

export const ASSESSMENT_PAGE_INDICATOR_SIZE = 44;
export const ASSESSMENT_PAGE_INDICATOR_THICKNESS = 3;
export const QUESTION_TYPES_WITH_NEXT_BUTTON: AssessmentQuestionType[] = [
  'textbox',
  'textarea',
  'checkbox',
  'date',
  'label',
];
export const TENANT_ID_QUERY_PARAM = 'tenantId';
export const SURVEY_EVENT_ID_QUERY_PARAM = 'surveyEventId';
export const ASSESSMENTS_LOADER_HEIGHT_DESKTOP_TABLET = 200;
export const ASSESSMENTS_LOADER_HEIGHT_MOBILE = 150;
export const ASSESSMENTS_PAGE_SIZE = 10;
export const HEADER_OFFSET_MOBILE = 88;
export const HEADER_OFFSET_DESKTOP_TABLET = 119;
export const ERROR_FLARE_TIMEOUT_MS = 5000;
export const ASSESSMENTS_AUTO_SAVE_DEBOUNCE_MS = 2000;
export const OFFLINE_ALERT_DEFAULT_DURATION_MS = 5000; // 5 seconds
export const ASSESSMENTS_OFFLINE_ALERT_DURATION_MS = 3600000; // 1 hour
export const ASSESSMENTS_OFFLINE_ALERT_MESSAGE = "You're offline, check your network connection.";
export const ASSESSMENTS_ONLINE_ALERT_MESSAGE = "You're back online.";
export type AlertVariant = 'offline-alert' | 'assessment-offline-alert';
export const ASSESSMENT_ACCESS_VALUE_TENANT_ID = 'TenantId';
export const ASSESSMENT_ACCESS_VALUE_FIRST_NAME = 'FirstName';
export const ASSESSMENT_ACCESS_VALUE_CLIENT_PATIENT_ID = 'ClientPatientId';
export const ASSESSMENT_ACCESS_VALUE_ASSESSMENT_ID = 'AssessmentId';
export const ASSESSMENT_ACCESS_VALUE_ACTIVITY_ID = 'ActivityId';
export const ASSESSMENT_ACCESS_VALUE_ACTIVITY_STATUS = 'ActivityStatus';
export const ASSESSMENT_ACCESS_VALUE_ASSESSMENT_STATUS = 'AssessmentStatus';
export const ASSESSMENTS_MIN_DATE = '1700-01-01';
export const ASSESSMENTS_MAX_DATE = '9999-12-31';
export const ASSESSMENTS_DEFAULT_ANSWER_PLACEHOLDER = 'Write your answer';
export const ERROR_TEXT =
  "We can't find the page you're looking for.\nThe link may be incorrect or no longer active.";
export const ASSESSMENTS_INACTIVITY_TIMEOUT_WARNING_DURATION_MS = 10 * 60 * 1000; // 10 minutes
export const ASSESSMENTS_WARNING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
export const ASSESSMENTS_TIMER_SECONDS = ASSESSMENTS_WARNING_TIMEOUT_MS / 1000; // 5 minutes in seconds

// Native (mobile app webview) bridge
export const ASSESSMENTS_NATIVE_BRIDGE_TIMEOUT_MS = 5000; // fall back to the verify page if native never responds
// How often the inbound queue is polled (see utils/nativeBridgeQueue.ts) - decoupled from React's
// render cycle entirely, so this never runs on every rerender, only on this fixed cadence.
export const ASSESSMENTS_NATIVE_BRIDGE_POLL_INTERVAL_MS = 150;
// Single generic entry point native calls for every inbound event (see AssessmentsNativeInboundEventType) -
// there's no dedicated per-event global, so adding a new inbound event never requires touching the bootstrap script.
export const ASSESSMENTS_NATIVE_BRIDGE_GLOBAL = 'postAssessmentsNativeEvent';
// Same name registered on both platforms: iOS's WKScriptMessageHandler name and Android's
// addJavascriptInterface name are each arbitrary strings the native side picks - keeping them
// identical avoids two constants (and two names) for what is conceptually one bridge.
export const ASSESSMENTS_NATIVE_BRIDGE_NAME = 'AssessmentsBridge';

// Who delivered the assessment auth: the native apps, or 'member-web' when the page is used
// directly in a browser (the default when no source is provided).
export type AssessmentSource = 'member-web' | 'ios' | 'android';

export enum AssessmentsNativeInboundEventType {
  SET_AUTH = 'SET_AUTH',
  ASSESSMENT_CLOSE_REQUESTED = 'ASSESSMENT_CLOSE_REQUESTED',
  // Native's answer to REFRESH_TOKEN once it has refreshed the token - carries only the new token
  // and its expiresAt.
  UPDATE_TOKEN = 'UPDATE_TOKEN',
}

// Events the page sends OUT to the native app via notifyNativeApp (see utils/nativeBridge.ts).
export enum AssessmentsNativeOutboundEventType {
  ASSESSMENT_SUBMITTED = 'ASSESSMENT_SUBMITTED',
  ASSESSMENT_SAVED_AND_CLOSED = 'ASSESSMENT_SAVED_AND_CLOSED',
  // Sent shortly before the access token's expiresAt (see getRefreshDelayMs) to ask native to
  // refresh the access token. Native replies immediately with {status: 'waiting'} and delivers the new token
  // later as an UPDATE_TOKEN inbound event (see AssessmentsNativeReplyMap in utils/nativeBridge.ts).
  REFRESH_TOKEN = 'REFRESH_TOKEN',
  // Sent automatically by consumeNativeBridgeQueue after handing each inbound event to its
  // consumer, so native knows that specific event was received off the queue.
  EVENT_CONSUMED = 'EVENT_CONSUMED',
}

export enum AssessmentStatus {
  inProgress = 'in-progress',
  completed = 'completed',
  amended = 'amended',
  enteredInError = 'entered-in-error',
  stopped = 'stopped',
}

export const ASSESSMENTS_VALIDATION_ERRORS = {
  DATE_INVALID: 'Date is invalid.',
  FIELD_REQUIRED: 'Field is required.',
} as const;

export const DEFAULT_ASSESSMENT_CARD_NAME = 'Comprehensive Assessment';

// Authenticated assessments: the token cannot be extended, so a new one is exchanged before it expires
export const ASSESSMENTS_TOKEN_REFRESH_BUFFER_MS = 60 * 1000; // 1 minute before expiry
export const ASSESSMENTS_TOKEN_MIN_REFRESH_DELAY_MS = 5 * 1000; // 5 seconds
export const ASSESSMENTS_TOKEN_REFRESH_MAX_ATTEMPTS = 3; // background refresh only; tried back to back before giving up
